<script lang="ts">
export interface AppTableInstance {
    exportCurrentViewToCsv(): boolean;
}

/**
 * Column style helper — use with v-bind on PrimeVue Column.
 * Applies the same style to header, body, and col so they always match.
 *
 * @param align    - 'right' to right-align header + cells
 * @param shrink   - true → width:1px + nowrap (fit content, useful for action cols in spread mode)
 * @param fill     - true → width:100% (spacer / action col in stacked mode)
 * @param minWidth - e.g. '12rem' to set a column minimum width
 */
export function col(opts: {
    align?: 'right';
    shrink?: boolean;
    fill?: boolean;
    minWidth?: string;
} = {}) {
    const s: Record<string, string> = {};
    if (opts.align) s.textAlign = opts.align;
    if (opts.shrink) { s.width = '1px'; s.whiteSpace = 'nowrap'; }
    if (opts.fill) s.width = '100%';
    if (opts.minWidth) s.minWidth = opts.minWidth;
    return { style: { ...s }, headerStyle: { ...s }, bodyStyle: { ...s } };
}

export default { inheritAttrs: false };
</script>

<script setup lang="ts">
/* eslint-env vue/setup-compiler-macros */
/* eslint-disable import/first, @typescript-eslint/no-explicit-any */
import { ref, computed } from 'vue';
import DataTable, { DataTablePageEvent, DataTableSortEvent } from 'primevue/datatable';
import Select from 'primevue/select';
import Skeleton from 'primevue/skeleton';

interface Props {
    value?: unknown[];
    paginator?: boolean;
    rows?: number;
    rowsPerPageOptions?: number[];
    loading?: boolean;
    paginatorLoading?: boolean;
    dataKey?: string;
    spread?: boolean;
    totalRecords?: number;
    pageLinkSize?: number;
    lazy?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
    value: () => [],
    paginator: true,
    rows: 10,
    rowsPerPageOptions: () => [10, 25, 50],
    loading: false,
    paginatorLoading: false,
    dataKey: 'id',
    spread: false,
    totalRecords: 0,
    lazy: false,
});

defineEmits<{
    (e: 'page', pageEvent: DataTablePageEvent): void;
    (e: 'sort', sortEvent: DataTableSortEvent): void;
}>();

const dt = ref<InstanceType<typeof DataTable> | null>(null);

const numberOfPages = computed(() => {
    return Math.ceil(props.totalRecords / (props.rows || 10));
});

const effectivePaginator = computed(() => {
    if (!props.paginator) return false;
    const count = props.lazy ? props.totalRecords : (props.value?.length ?? 0);
    return count > 0;
});

function getPageLinks(page: number): (number | '...')[] {
    const pageCount = numberOfPages.value;
    if (pageCount <= 1) return pageCount === 1 ? [1] : [];
    const current = page + 1;
    const visible = new Set<number>();
    visible.add(1);
    visible.add(pageCount);
    for (let i = Math.max(2, current - 1); i <= Math.min(pageCount - 1, current + 2); i += 1) {
        visible.add(i);
    }
    const sorted = [...visible].sort((a, b) => a - b);
    const result: (number | '...')[] = [];
    for (let i = 0; i < sorted.length; i += 1) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push('...');
        result.push(sorted[i]);
    }
    return result;
}

function exportCurrentViewToCsv(): boolean {
    if (!dt.value) return false;

    try {
        const dataTable = dt.value as InstanceType<typeof DataTable> & {
            dataToRender?: () => unknown[];
        };
        const exportRows = dataTable.dataToRender?.() ?? props.value ?? [];

        dt.value.exportCSV({ selectionOnly: false }, exportRows);
        return true;
    } catch {
        return false;
    }
}

defineExpose({
    exportCurrentViewToCsv,
});
</script>

<template>
    <div :class="['app-table', { 'app-table--spread': spread }]">
        <DataTable
            ref="dt"
            v-bind="$attrs"
            :value="value"
            :paginator="effectivePaginator"
            :rows="rows"
            :rows-per-page-options="rowsPerPageOptions"
            :loading="loading"
            :data-key="dataKey"
            :totalRecords="totalRecords"
            :lazy="lazy"
            @page="(event: DataTablePageEvent) => $emit('page', event)"
            @sort="(event: DataTableSortEvent) => $emit('sort', event)"
        >
            <template
                #paginatorcontainer="{
                    first, last, page,
                    rows: currentRows,
                    prevPageCallback, nextPageCallback,
                    rowChangeCallback, changePageCallback,
                }"
            >
                <div class="app-paginator">
                    <Select
                        :model-value="currentRows"
                        :options="rowsPerPageOptions"
                        aria-label="Rows per page"
                        @change="(e: any) => rowChangeCallback(e.value)"
                    />
                    <div class="app-paginator__right">
                        <span class="app-paginator__report">
                            Showing <b>{{ first }}</b> to <b>{{ last }}</b>
                            of
                            <Skeleton
                                v-if="paginatorLoading"
                                width="40px"
                                height="15px"
                                border-radius="4px"
                            />
                            <b v-else>{{ totalRecords }}</b>
                            entries
                        </span>
                        <button
                            class="app-paginator__nav app-paginator__nav--prev"
                            :disabled="page === 0"
                            @click="prevPageCallback"
                        />
                        <Skeleton
                            v-if="paginatorLoading"
                            width="88px"
                            height="15px"
                            border-radius="4px"
                        />
                        <template v-else>
                            <template
                                v-for="(link, idx) in getPageLinks(page)"
                                :key="idx"
                            >
                                <span v-if="link === '...'" class="app-paginator__ellipsis">
                                    ...
                                </span>
                                <button
                                    v-else
                                    class="app-paginator__page"
                                    :class="{ 'is-active': (link as number) - 1 === page }"
                                    @click="changePageCallback((link as number) - 1)"
                                >{{ link }}</button>
                            </template>
                        </template>
                        <button
                            class="app-paginator__nav app-paginator__nav--next"
                            :disabled="page === numberOfPages - 1"
                            @click="nextPageCallback"
                        />
                    </div>
                </div>
            </template>
            <template v-for="(_, name) in $slots" :key="name" #[name]="slotData">
                <slot :name="(name as string)" v-bind="(slotData as any) ?? {}" />
            </template>
        </DataTable>
    </div>
</template>

<style lang="scss" scoped>
.app-table {
    background: $white-color;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    overflow: hidden;

    // ── Table base
    :deep(.p-datatable) {
        --p-datatable-header-cell-padding: 18px 30px;
        --p-datatable-column-title-font-weight: 300;
        --p-datatable-body-cell-padding: 20px 30px;
        --p-datatable-row-color: #{$black-color};
        --p-datatable-row-hover-color: #{$black-color};
        --p-datatable-row-selected-color: #{$black-color};
        font-family: $font-family-archivo !important;
        background: transparent !important;
    }

    :deep(.p-datatable-table-container) {
        overflow-x: auto;
    }

    // ── Header cells
    :deep(.p-datatable-header-cell) {
        background: $main-background-color !important;
        border: none !important;
        white-space: nowrap;
        box-shadow: none !important;
    }

    :deep(.p-datatable-column-title) {
        font-size: 15px !important;
        color: rgba($black-color, 0.6) !important;
    }

    :deep(.p-datatable-column-header-content) {
        width: 100%;
    }

    :deep(.p-datatable-header-cell[style*='text-align: right'] .p-datatable-column-header-content),
    :deep(.p-datatable-header-cell[style*='text-align:right'] .p-datatable-column-header-content) {
        justify-content: flex-end;
    }

    // ── Stacked mode (default) — columns shrink to content
    // Exclude fill columns (width:100%) so they still absorb remaining space
    :deep(.p-datatable-header-cell:not([style*='width: 100%'])),
    :deep([data-pc-section='bodycell']:not([style*='width: 100%'])) {
        width: 1px !important;
        white-space: nowrap !important;
        word-break: normal !important;
        overflow-wrap: normal !important;
    }

    // ── Spread mode — table fills available width, cells size naturally
    &--spread :deep(.p-datatable-table) {
        table-layout: auto;
        width: 100%;
    }

    &--spread :deep(.p-datatable-header-cell) {
        width: auto !important;
    }

    &--spread :deep([data-pc-section='bodycell']) {
        width: auto !important;
        white-space: normal !important;
    }

    // ── Custom sort icon
    :deep(.p-datatable-sort-icon) {
        display: none !important;
    }

    // Render triangles on the wrapper span via pseudo-elements
    :deep([data-pc-section='sort']) {
        display: inline-flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 3px;
        width: 8px;
        margin-left: 6px;
        flex-shrink: 0;

        &::before,
        &::after {
            content: '';
            display: block;
            width: 0;
            height: 0;
            border-style: solid;
        }

        // up triangle
        &::before {
            border-width: 0 4px 5px 4px;
            border-color: transparent transparent $table-sort-icon-inactive transparent;
        }

        // down triangle
        &::after {
            border-width: 5px 4px 0 4px;
            border-color: $table-sort-icon-inactive transparent transparent transparent;
        }
    }

    // ascending
    :deep([aria-sort='ascending'] [data-pc-section='sort']) {
        &::before {
            border-bottom-color: $main-orange;
        }
    }

    // descending
    :deep([aria-sort='descending'] [data-pc-section='sort']) {
        &::after {
            border-top-color: $main-orange;
        }
    }

    // ── Body rows & cells
    :deep(.p-datatable-tbody > tr) {
        background: transparent !important;

        &:last-child > td {
            border-bottom: none !important;
        }

        &:hover > td {
            background: rgba($border-gray-color, 0.4) !important;
        }
    }

    :deep(.p-datatable-tbody > tr > td) {
        font-weight: 500 !important;
    }

    :deep(.p-datatable-tbody > tr > td:has(> .p-button)) {
        padding-top: 0 !important;
        padding-bottom: 0 !important;
    }

    // ── Empty state
    :deep(.p-datatable-empty-message td) {
        text-align: center !important;
        padding: 32px 20px !important;
        color: rgba($black-color, 0.45) !important;
        font-family: $font-family-archivo !important;
        font-size: 15px !important;
        border: none !important;
    }

    // ── Paginator wrapper
    :deep(.p-paginator) {
        padding: 0 !important;
        border: none !important;
        background: transparent !important;
        display: block !important;
    }

    :deep(.p-datatable-paginator-bottom) {
        border: none;
    }

    // ── Headless paginator
    .app-paginator {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 25px 30px;
        border-top: 1px solid $border-gray-color;

        :deep(.p-select) {
            min-width: $paginator-rpp-min-width;
            height: $paginator-control-height;
            border: 1px solid $paginator-border-color;
            border-radius: $paginator-control-radius;
            background: $white-color;
            box-shadow: none;
        }

        :deep(.p-select-label) {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 0 14px;
            font-family: $font-family-roboto;
            font-size: 14px;
            font-weight: 400;
            color: $black-color;
        }

        :deep(.p-select-label)::after {
            content: 'per page';
        }

        :deep(.p-select-dropdown) {
            display: flex;
            align-items: center;
            padding-right: 14px;
            color: $black-color;

            .p-icon {
                width: 14px;
                height: 14px;
            }
        }

        &__right {
            display: flex;
            align-items: center;
            gap: 0;
        }

        &__report {
            display: inline-flex;
            align-items: center;
            gap: 4px;
            font-family: $font-family-roboto;
            font-size: 15px;
            font-weight: 400;
            color: $black-color;
            white-space: nowrap;
            margin-right: 25px;
        }

        &__nav {
            width: $paginator-nav-button-width;
            height: $paginator-control-height;
            border: 1px solid $paginator-border-color;
            border-radius: $paginator-control-radius;
            background: $white-color;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: border-color 0.15s;

            &::before {
                content: '';
                display: block;
                width: 8px;
                height: 8px;
                border-style: solid;
                border-color: $main-orange;
            }

            &--prev::before {
                border-width: 2px 0 0 2px;
                transform: rotate(-45deg);
                margin-left: 3px;
            }

            &--next::before {
                border-width: 2px 2px 0 0;
                transform: rotate(45deg);
                margin-right: 3px;
            }

            &--prev {
                margin-right: 25px;
            }

            &--next {
                margin-left: 25px;
            }

            &:disabled {
                opacity: 0.45;
                cursor: not-allowed;
            }

            &:not(:disabled):hover {
                border-color: darken($paginator-border-color, 6%);
            }
        }

        &__page {
            font-family: $font-family-archivo;
            font-size: 15px;
            font-weight: 400;
            color: $black-color;
            background: transparent;
            border: none;
            padding: 0 6px;
            cursor: pointer;
            text-decoration: none;
            line-height: 1;

            &.is-active {
                text-decoration: underline;
                text-underline-offset: 2px;
            }

            &:hover {
                opacity: 0.7;
            }
        }

        &__ellipsis {
            font-family: $font-family-archivo;
            font-size: 15px;
            color: $black-color;
            line-height: 1;
        }
    }
}
</style>
